import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  AdminApiError,
  adminApi,
  googleLoginUrl,
  type AdminSession,
  type PendingStatus,
  type SessionIssued,
} from "../api";

/**
 * भाग E4 login redesign: Google SSO (restricted server-side to a single
 * allowed account) is the first factor; a password is the second. This
 * screen never asks for (or has a field for) an application `X-API-Key`,
 * and never asks for a Google password itself — that happens entirely on
 * accounts.google.com.
 *
 * Two states, driven by the `?stage=password` query param
 * `/admin/auth/callback` redirects back with:
 *
 * - no `stage` — show "Sign in with Google".
 * - `stage=password` — a Google identity was just verified (an httponly
 *   pending-ticket cookie proves it); ask `/admin/auth/pending` whether this
 *   is a first login (show a "set password" form) or a normal one (show a
 *   plain password form), then post to `/admin/auth/set-password` or
 *   `/admin/auth/password` respectively.
 */
export function Login({ onSignedIn }: { onSignedIn: (session: AdminSession) => void }): JSX.Element {
  const [searchParams] = useSearchParams();
  const stage = searchParams.get("stage");

  const [pending, setPending] = useState<PendingStatus | null>(null);
  const [checking, setChecking] = useState(stage === "password");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (stage !== "password") return;
    let cancelled = false;
    setChecking(true);
    adminApi
      .get<PendingStatus>("/admin/auth/pending")
      .then((status) => {
        if (!cancelled) setPending(status);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message =
          err instanceof AdminApiError ? err.message : "Could not verify Google sign-in.";
        setError(message);
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });
    return () => {
      cancelled = true;
    };
  }, [stage]);

  async function handlePasswordSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (pending?.must_set_password && password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      const path = pending?.must_set_password
        ? "/admin/auth/set-password"
        : "/admin/auth/password";
      const issued = await adminApi.post<SessionIssued>(path, { password });
      onSignedIn({
        sessionToken: issued.session_token,
        expiresAt: issued.expires_at,
        adminRole: issued.admin_role,
        email: issued.email,
      });
    } catch (err) {
      const message =
        err instanceof AdminApiError ? err.message : "Sign-in failed. Please try again.";
      setError(message);
      // The server has finished with this Google sign-in: either the ticket is
      // spent (a login, or three wrong passwords) or the account is locked.
      // Retrying the form would only repeat the refusal, so go back to the
      // "sign in with Google again" view, keeping the server's explanation.
      if (
        err instanceof AdminApiError &&
        (err.code === "admin_pending_ticket_invalid" || err.code === "admin_account_locked")
      ) {
        setPending(null);
        setPassword("");
        setConfirmPassword("");
      }
    } finally {
      setBusy(false);
    }
  }

  if (stage !== "password") {
    return (
      <div className="login-shell">
        <div className="login-card">
          <h1>AstraSpace Admin Control Plane</h1>
          <p>
            Google sign-in restricted to a single account, followed by a password — reachable
            only from your allowlisted IP.
          </p>
          {error && <div className="error-banner">{error}</div>}
          <a className="google-signin-button" href={googleLoginUrl()}>
            Sign in with Google
          </a>
        </div>
      </div>
    );
  }

  if (checking) {
    return (
      <div className="login-shell">
        <div className="login-card">
          <h1>AstraSpace Admin Control Plane</h1>
          <p>Verifying Google sign-in…</p>
        </div>
      </div>
    );
  }

  if (!pending) {
    return (
      <div className="login-shell">
        <div className="login-card">
          <h1>AstraSpace Admin Control Plane</h1>
          {error && <div className="error-banner">{error}</div>}
          <p>Your Google sign-in could not be verified, or has expired.</p>
          <a className="google-signin-button" href={googleLoginUrl()}>
            Sign in with Google again
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="login-shell">
      <div className="login-card">
        <h1>AstraSpace Admin Control Plane</h1>
        <p>
          Signed in to Google as <strong>{pending.email}</strong>.
        </p>
        {pending.must_set_password && (
          <p>First sign-in — set a password (12+ characters, upper + lower + digit + symbol).</p>
        )}
        {error && <div className="error-banner">{error}</div>}
        <form onSubmit={handlePasswordSubmit}>
          <label htmlFor="password">{pending.must_set_password ? "New password" : "Password"}</label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
          />
          {pending.must_set_password && (
            <>
              <label htmlFor="confirm-password">Confirm password</label>
              <input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
            </>
          )}
          <button type="submit" disabled={busy}>
            {busy ? "Verifying…" : pending.must_set_password ? "Set password & sign in" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
